import { useState } from "react";
import { createWorkspace } from "@/lib/api/endpoints";
import { writeActiveCustomerCookie } from "@/lib/mapper/active-customer-cookie";

/**
 * First-run screen for a signed-in user who belongs to no workspace yet:
 * create one (they become its OWNER), or accept a pending invitation.
 */
export function CreateWorkspace({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const workspace = await createWorkspace(trimmed);
      writeActiveCustomerCookie(workspace.id);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the workspace.");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md px-5 py-16">
      <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-100">Create your workspace</h1>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
        A workspace holds your workbooks and templates. Name it after your store or brand; you can
        invite teammates afterwards.
      </p>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Apparel"
          autoFocus
          className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500 dark:border-border dark:bg-surface dark:text-zinc-100"
        />
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="w-full rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-zinc-700 disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-950"
        >
          {busy ? "Creating…" : "Create workspace"}
        </button>
        {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
      </form>
    </div>
  );
}
